import React from "react";
import { Draggable } from "@hello-pangea/dnd";
import { ArrowRight, CalendarClock, FileSearch } from "lucide-react";
import { fmtCompact, daysUntil } from "@/lib/format";
import { HealthIndicator, UserAvatar, CompanyLogo } from "@/components/common";
import { cn } from "@/lib/utils";

export const OWNER_COLORS = { u_david: "#e8664e", u_erik: "#2f6f8f", u_maria: "#3f7d5c", u_jonas: "#8a6d3b" };

export function CloseChip({ date, status }) {
  if (!date || status !== "open") return null;
  const d = daysUntil(date);
  if (d === null) return null;
  const overdue = d < 0;
  const soon = d >= 0 && d <= 14;
  return (
    <span className={cn("inline-flex items-center gap-1 text-[11.5px] tabular", overdue ? "font-medium text-[hsl(var(--status-bad))]" : soon ? "font-medium text-[hsl(var(--status-warn))]" : "text-muted-foreground")}>
      <CalendarClock className="h-3 w-3" /> {overdue ? `${Math.abs(d)}d overdue` : d === 0 ? "Today" : `${d}d`}
    </span>
  );
}

function CardBody({ o, onOpen, className, dragging, innerRef, ...rest }) {
  return (
    <div
      ref={innerRef}
      {...rest}
      onClick={() => onOpen?.(o)}
      data-testid={`pipeline-card-${o.id}`}
      className={cn(
        "group mb-2 cursor-pointer rounded-xl border border-black/[0.07] bg-white p-3.5 shadow-[0_1px_2px_hsl(30_12%_18%/0.04)] transition-[box-shadow,border-color,transform] duration-150 hover:-translate-y-px hover:border-black/[0.14] hover:shadow-md-soft",
        dragging && "rotate-[0.6deg] border-primary/40 shadow-float",
        className
      )}
    >
      <div className="flex items-start gap-2.5">
        <CompanyLogo name={o.account_name} domain={o.account_website} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[13.5px] font-semibold tracking-[-0.01em]">{o.account_name}</span>
            {o.type === "tender" && <span className="inline-flex items-center gap-1 rounded-md border border-black/[0.08] px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground"><FileSearch className="h-2.5 w-2.5" /> Tender</span>}
          </div>
          <div className="truncate text-[12.5px] text-muted-foreground">{o.name}</div>
        </div>
      </div>
      <div className="mt-3 flex items-baseline justify-between">
        <span className="text-[16px] font-semibold tabular tracking-[-0.02em]">{fmtCompact(o.value)}</span>
        {o.gm_pct !== null && o.gm_pct !== undefined ? <span className={cn("text-[11.5px] tabular", o.gm_pct < 30 ? "font-medium text-[hsl(var(--status-warn))]" : "text-muted-foreground")}>GM {Math.round(o.gm_pct)}%</span> : null}
      </div>
      {o.next_step ? (
        <div className="mt-2 flex items-start gap-1.5 text-[12px] leading-snug text-muted-foreground">
          <ArrowRight className="mt-[3px] h-3 w-3 shrink-0 text-muted-foreground/70" />
          <span className="line-clamp-1">{o.next_step}</span>
        </div>
      ) : o.status === "open" ? (
        <div className="mt-2 text-[12px] font-medium text-primary">No next step</div>
      ) : null}
      <div className="mt-3 flex items-center justify-between border-t border-black/[0.05] pt-2.5">
        <div className="flex items-center gap-2">
          <UserAvatar name={o.owner_name} initials={o.owner_initials} color={OWNER_COLORS[o.owner_id] || "#111"} size="xs" />
          <CloseChip date={o.expected_close_date} status={o.status} />
        </div>
        <HealthIndicator health={o.health} reason={o.reason} compact />
      </div>
    </div>
  );
}

/**
 * One opportunity on the board. `draggable` (default true) wraps it in a dnd Draggable - set false to render
 * it as a plain card (previews, lists outside a DragDropContext).
 */
export function OppCard({ o, index = 0, onOpen, draggable = true, className }) {
  if (!draggable) return <CardBody o={o} onOpen={onOpen} className={className} />;
  return (
    <Draggable draggableId={o.id} index={index}>
      {(prov, snap) => <CardBody o={o} onOpen={onOpen} className={className} dragging={snap.isDragging} innerRef={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps} />}
    </Draggable>
  );
}
